import { SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { Payment, PaymentList } from '@/models/api/payment/payment'
import { sortPaymentList } from '@spa/components/organisms/order/edit/payment/specifications/select-option-ordering'
import { CreditcardModel } from '@spa/store/modules/creditcard/types'

export default class PaymentEditViewModel {

  constructor(
    public readonly availablePaymentList: PaymentList,
    public readonly creditCard: PaymentEditCreditCardViewModel,
    public readonly selectedPaymentId: string,
    public readonly selectedOptions: SelectOptionList,
    public readonly caption: string,
    public readonly submitButtonLabel: string,
    public readonly cancelButtonLabel: string,
    public readonly showCancelButton: boolean
  ) {}

  get availablePaymentListIsEmpty(): boolean {
    return this.availablePaymentList.isEmpty
  }

  get availablePaymentListExcludeCreditCard(): Payment[] {
    return this.availablePaymentList.list.filter(p => !p.isCreditCard())
  }

  get isSomePaymentAvailableExcludeCreditCard(): boolean {
    return this.availablePaymentListExcludeCreditCard.length > 0
  }

  get creditCardIsAvailable(): boolean {
    return this.availablePaymentList.some(payment => payment.isCreditCard())
  }
}

export class PaymentEditCreditCardViewModel {

  constructor(
    public readonly creditCards: CreditcardModel[],
    public readonly useCallback: (creditCard: CreditcardModel) => () => Promise<void>,
    public readonly removeCallback: (creditCard: CreditcardModel) => () => Promise<void>,
    public readonly registerCallback: () => void,
    public readonly showSecurityCode: boolean,
    public readonly selectedId: string | null,
    public readonly registerLimit: number | null,
    public readonly loadFailed: boolean | null,
    private readonly _errorMessage: string
  ) {}

  get list(): CreditcardModel[] {
    return this.creditCards
  }

  get listIsEmpty(): boolean {
    return this.list.length === 0
  }

  get canRegister(): boolean {
    if (this.registerLimit === null) {
      return true
    }

    return this.list.length < this.registerLimit
  }

  get errorMessage(): string {
    if (!this.canRegister) {
      return this._errorMessage
    }

    return ''
  }
}

export class PaymentEditViewModelFactory {

  selectedPaymentId: string = ''
  selectedOptions: SelectOptionList = SelectOptionList.empty()
  creditCardSelectedId: string = ''
  creditCardRegisterLimit: number | null = null
  creditCardLoadFailed: boolean = false
  caption: string = ''
  submitButtonLabel: string = 'お支払い方法を確定する'
  cancelButtonLabel: string = '戻る'
  showCancelButton: boolean = false
  errorMessage: string = 'クレジットカード登録数の上限に達しています。カードを新たに登録する場合、不要なカードを削除してください。'

  /**
   * 必須のパラメータはコンストラクタで受け、任意のパラメータはFactory外部から直接プロパティを修正できるようにする。
   * 必須のパラメータとは、デフォルトの挙動が定義されるべきでないものである。
   *
   * builderパターンによる実装は、↓の理由で採用しなかった。
   * @see https://github.com/my-color/front/pull/4836#discussion_r575764797
   */
  constructor(
    private readonly availablePaymentList: PaymentList,
    private readonly creditCardList: CreditcardModel[],
    private readonly creditCardUseCallback: (creditCard: CreditcardModel) => () => Promise<void>,
    private readonly creditCardRemoveCallback: (creditCard: CreditcardModel) => () => Promise<void>,
    private readonly creditCardRegisterCallback: () => void,
    private readonly creditCardShowSecurityCode: boolean
  ) {}

  create(): PaymentEditViewModel {
    return new PaymentEditViewModel(
      sortPaymentList(this.availablePaymentList),
      new PaymentEditCreditCardViewModel(
        this.creditCardList,
        this.creditCardUseCallback,
        this.creditCardRemoveCallback,
        this.creditCardRegisterCallback,
        this.creditCardShowSecurityCode,
        this.creditCardSelectedId,
        this.creditCardRegisterLimit,
        this.creditCardLoadFailed,
        this.errorMessage
      ),
      this.selectedPaymentId,
      this.selectedOptions,
      this.caption,
      this.submitButtonLabel,
      this.cancelButtonLabel,
      this.showCancelButton
    )
  }
}
